const showDebugCustomAtpDateOutput = false;

const ATP_PHONE_DISPLAY = '(352) 728-5600';
const ATP_PHONE_TEL = '+13527285600';
const CALL_FOR_AVAILABILITY_HTML = `Call for Availability <a href="tel:${ATP_PHONE_TEL}">${ATP_PHONE_DISPLAY}</a>`;
const CALL_FOR_AVAILABILITY_TEXT = `Call for Availability ${ATP_PHONE_DISPLAY}`;

const PROP_ESTIMATED_DELIVERY = 'Estimated delivery';
const PROP_ATP_SKU = '_ATP SKU';
const PROP_ATP_INVENTORY = '_ATP Inventory';

/** @type {{ formId?: string, variants?: Record<string, VariantAtpMeta> } | null} */
let atpData = null;
/** @type {AbortController | null} */
let atpAbortController = null;
let atpRequestId = 0;
let cartHooksInstalled = false;
let cartChangeEnrichInstalled = false;
/** Prevent our own /cart/change writes from re-entering sync. */
let atpCartPropertyUpdateInFlight = false;
/** Bumped on each cart:change sync so stale async work is discarded. */
let atpSyncGeneration = 0;
/** @type {Map<string, { handle: string, variants: Record<string, VariantAtpMeta> }>} */
const atpVariantFactsCache = new Map();

const ATP_CART_SYNC_EVENTS = new Set([
  'variant:add',
  'line-item:change',
  'quick-order-list:add',
]);

/** @type {Record<string, string>} */
let pendingAtpLineProperties = {};

/**
 * @typedef {object} VariantAtpMeta
 * @property {string | null} sku
 * @property {number} inventoryQuantity
 * @property {string} inventoryPolicy
 */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAtp);
} else {
  initAtp();
}

function initAtp() {
  installCartAtpHooks();

  const mount = findPrimaryAtpMount();
  if (!mount) {
    return;
  }

  const formId = mount.dataset.formId;
  if (!formId || !activateAtpForm(formId)) {
    return;
  }

  document.addEventListener('variant:change', onVariantChange);
  document.addEventListener('input', onQuantityEvent, true);
  document.addEventListener('change', onQuantityEvent, true);

  updateAtpDisplay(formId);
}

/**
 * Prefer a PDP mount; otherwise the first ATP mount on the page.
 * @returns {HTMLElement | null}
 */
function findPrimaryAtpMount() {
  const mounts = [...document.querySelectorAll('.custom-atp-date[data-form-id]')];
  if (!mounts.length) {
    return null;
  }
  return (
    mounts.find((mount) => !mount.closest('quick-buy-modal')) || mounts[0]
  );
}

/**
 * @param {string} formId
 * @returns {boolean}
 */
function activateAtpForm(formId) {
  if (atpData?.formId && atpData.formId !== formId) {
    pendingAtpLineProperties = {};
  }

  const dataEl = document.getElementById(`product-atp-data-${formId}`);
  if (!dataEl) {
    showDebugCustomAtpDateOutput &&
      console.warn('customAtpDate: missing product-atp-data for', formId);
    return false;
  }

  try {
    atpData = JSON.parse(dataEl.textContent);
  } catch (error) {
    console.error('customAtpDate: failed to parse product-atp-data', error);
    return false;
  }

  return true;
}

/**
 * Cart/ATC hooks that do not wrap /cart/add (that broke drawer section re-renders).
 */
function installCartAtpHooks() {
  if (cartHooksInstalled) {
    return;
  }
  cartHooksInstalled = true;

  document.addEventListener('submit', onProductFormSubmitCapture, true);
  document.addEventListener('cart:change', onCartChangeForAtp);
  document.addEventListener('dialog:after-show', onQuickBuyAfterShow, true);
  document.addEventListener('dialog:after-hide', onQuickBuyAfterHide, true);

  // Cart page reloads after qty change, so enrich that request before it completes.
  if (window.themeVariables?.settings?.pageType === 'cart') {
    installCartPageChangeEnricher();
  }
}

/**
 * @param {Event} event
 */
function onQuickBuyAfterShow(event) {
  const modal = event.target;
  if (!(modal instanceof HTMLElement) || !modal.matches('quick-buy-modal')) {
    return;
  }

  const mount = modal.querySelector('.custom-atp-date[data-form-id]');
  const formId = mount?.dataset.formId;
  if (!formId || !activateAtpForm(formId)) {
    return;
  }

  updateAtpDisplay(formId);
}

/**
 * Restore PDP ATP context after closing quick-buy.
 * @param {Event} event
 */
function onQuickBuyAfterHide(event) {
  const modal = event.target;
  if (!(modal instanceof HTMLElement) || !modal.matches('quick-buy-modal')) {
    return;
  }

  const pdpMount = findPrimaryAtpMount();
  const formId = pdpMount?.dataset.formId;
  if (!formId || !activateAtpForm(formId)) {
    return;
  }
  updateAtpDisplay(formId);
}

/**
 * Re-apply pending ATP properties onto the active ATP product form before Prestige builds FormData.
 * Bare / card ATC forms rely on cart:change sync instead.
 * @param {SubmitEvent} event
 */
function onProductFormSubmitCapture(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const action = form.getAttribute('action') || '';
  if (!action.includes('/cart/add')) {
    return;
  }

  if (!form.id || form.id !== atpData?.formId) {
    return;
  }

  applyPendingPropertiesToForm(form);
}

/**
 * @param {HTMLFormElement} form
 */
function applyPendingPropertiesToForm(form) {
  Object.entries(pendingAtpLineProperties).forEach(([key, value]) => {
    if (value == null || value === '') {
      upsertFormProperty(form, key, null);
      return;
    }
    upsertFormProperty(form, key, value);
  });

  if (!(PROP_ESTIMATED_DELIVERY in pendingAtpLineProperties)) {
    upsertFormProperty(form, PROP_ESTIMATED_DELIVERY, null);
  }
}

/**
 * Single cart ATP pipeline: ensure facts, set properties for current qty, refresh drawer.
 * Generation token discards stale work when qty changes race.
 * @param {CustomEvent} event
 */
function onCartChangeForAtp(event) {
  if (window.themeVariables?.settings?.pageType === 'cart') {
    return;
  }

  const baseEvent = event.detail?.baseEvent;
  if (!ATP_CART_SYNC_EVENTS.has(baseEvent)) {
    return;
  }

  void syncAtpForCart(event.detail?.cart, baseEvent);
}

/**
 * @param {object | null | undefined} cart
 * @param {string} [baseEvent]
 */
async function syncAtpForCart(cart, baseEvent) {
  if (!cart?.items?.length) {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return;
  }

  const gen = ++atpSyncGeneration;
  let didWrite = false;

  try {
    for (const item of cart.items) {
      if (gen !== atpSyncGeneration) {
        return;
      }

      const itemWithFacts = await ensureVariantFactsOnItem(item);
      if (gen !== atpSyncGeneration) {
        return;
      }
      if (!itemWithFacts) {
        continue;
      }

      const nextProperties = await buildAtpPropertiesForLine(
        itemWithFacts,
        item.quantity
      );
      if (gen !== atpSyncGeneration) {
        return;
      }
      if (!nextProperties) {
        continue;
      }
      if (propertiesEqual(item.properties || {}, nextProperties)) {
        continue;
      }

      if (gen !== atpSyncGeneration) {
        return;
      }

      atpCartPropertyUpdateInFlight = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: nextProperties,
          }),
        });
        if (gen !== atpSyncGeneration) {
          return;
        }
        if (response.ok) {
          didWrite = true;
        }
      } finally {
        atpCartPropertyUpdateInFlight = false;
      }
    }

    if (gen !== atpSyncGeneration) {
      return;
    }

    if (didWrite || baseEvent === 'variant:add') {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  } catch (error) {
    showDebugCustomAtpDateOutput &&
      console.error('customAtpDate: syncAtpForCart failed', error);
    if (gen === atpSyncGeneration && (didWrite || baseEvent === 'variant:add')) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }
}

/**
 * Ensure line has _ATP Inventory (and SKU when available), using line props or section fetch.
 * @param {object} item
 * @returns {Promise<object | null>}
 */
async function ensureVariantFactsOnItem(item) {
  const existing = { ...(item.properties || {}) };
  const inventoryRaw = existing[PROP_ATP_INVENTORY];
  if (inventoryRaw != null && inventoryRaw !== '' && Number.isFinite(Number(inventoryRaw))) {
    if (!existing[PROP_ATP_SKU] && item.sku) {
      existing[PROP_ATP_SKU] = String(item.sku);
    }
    return { ...item, properties: existing };
  }

  const handle = getCartItemHandle(item);
  if (!handle) {
    return fallbackFactsFromSku(item, existing);
  }

  const facts = await fetchVariantFactsForHandle(handle);
  const variantId = String(item.id);
  const meta = facts?.variants?.[variantId];
  if (!meta || !Number.isFinite(Number(meta.inventoryQuantity))) {
    return fallbackFactsFromSku(item, existing);
  }

  existing[PROP_ATP_INVENTORY] = String(meta.inventoryQuantity);
  const sku = meta.sku || item.sku;
  if (sku) {
    existing[PROP_ATP_SKU] = String(sku);
  }

  return { ...item, properties: existing };
}

/**
 * When section facts are unavailable, still attach ATP using SKU + inventory 0.
 * @param {object} item
 * @param {Record<string, string>} existing
 * @returns {object | null}
 */
function fallbackFactsFromSku(item, existing) {
  const sku = item.sku || existing[PROP_ATP_SKU];
  if (!sku) {
    return null;
  }
  existing[PROP_ATP_SKU] = String(sku);
  existing[PROP_ATP_INVENTORY] = '0';
  return { ...item, properties: existing };
}

/**
 * @param {object} item
 * @returns {string | null}
 */
function getCartItemHandle(item) {
  if (item.handle) {
    return String(item.handle);
  }
  if (item.product_handle) {
    return String(item.product_handle);
  }
  if (item.url) {
    try {
      const path = new URL(item.url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * @param {string} handle
 * @returns {Promise<{ handle: string, variants: Record<string, VariantAtpMeta> } | null>}
 */
async function fetchVariantFactsForHandle(handle) {
  if (atpVariantFactsCache.has(handle)) {
    return atpVariantFactsCache.get(handle) || null;
  }

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(
    `${root}products/${encodeURIComponent(handle)}?section_id=atp-variant-facts`
  );
  if (!response.ok) {
    return null;
  }

  const html = await response.text();
  const parsed = parseJsonFromSectionHtml(html);
  if (!parsed?.variants) {
    return null;
  }

  atpVariantFactsCache.set(handle, parsed);
  return parsed;
}

/**
 * @param {string} html
 * @returns {object | null}
 */
function parseJsonFromSectionHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const section = doc.querySelector('.shopify-section') || doc.body;
  const text = section?.textContent?.trim();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    const match = html.match(/\{[\s\S]*"variants"[\s\S]*\}/);
    if (!match) {
      return null;
    }
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  }
}

/**
 * Narrow /cart/change enrich for cart page only (page reloads after change).
 * Never wraps /cart/add.
 */
function installCartPageChangeEnricher() {
  if (cartChangeEnrichInstalled) {
    return;
  }
  cartChangeEnrichInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function atpCartPageFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    const method = (init?.method || 'GET').toUpperCase();

    if (
      url &&
      method === 'POST' &&
      url.includes('/cart/change') &&
      !url.includes('/cart/add') &&
      init?.body &&
      typeof init.body === 'string' &&
      !atpCartPropertyUpdateInFlight
    ) {
      try {
        const enrichedBody = await enrichCartChangeBody(init.body);
        if (enrichedBody !== init.body) {
          init = { ...init, body: enrichedBody };
        }
      } catch (error) {
        showDebugCustomAtpDateOutput &&
          console.error('customAtpDate: cart page enrich failed', error);
      }
    }

    return nativeFetch(input, init);
  };
}

/**
 * @param {string} bodyText
 * @returns {Promise<string>}
 */
async function enrichCartChangeBody(bodyText) {
  let body;
  try {
    body = JSON.parse(bodyText);
  } catch {
    return bodyText;
  }

  if (!body?.id || body.quantity == null) {
    return bodyText;
  }

  const cart = await fetchCartJson();
  const lineItem = cart?.items?.find((item) => item.key === body.id);
  if (!lineItem) {
    return bodyText;
  }

  const itemWithFacts = await ensureVariantFactsOnItem(lineItem);
  if (!itemWithFacts) {
    return bodyText;
  }

  const nextProperties = await buildAtpPropertiesForLine(
    itemWithFacts,
    Number(body.quantity)
  );
  if (!nextProperties) {
    return bodyText;
  }

  if (propertiesEqual(lineItem.properties || {}, nextProperties)) {
    return bodyText;
  }

  body.properties = nextProperties;
  return JSON.stringify(body);
}

/**
 * @param {object} lineItem
 * @param {number} quantity
 * @returns {Promise<Record<string, string> | null>}
 */
async function buildAtpPropertiesForLine(lineItem, quantity) {
  const existingProperties = { ...(lineItem.properties || {}) };
  const sku = existingProperties[PROP_ATP_SKU] || lineItem.sku || '';
  const inventoryRaw = existingProperties[PROP_ATP_INVENTORY];
  const inventoryQuantity = Number(inventoryRaw);

  if (inventoryRaw == null || inventoryRaw === '' || !Number.isFinite(inventoryQuantity)) {
    return null;
  }

  if (!Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  const nextProperties = { ...existingProperties };
  if (sku) {
    nextProperties[PROP_ATP_SKU] = String(sku);
  }
  nextProperties[PROP_ATP_INVENTORY] = String(inventoryQuantity);

  if (shouldSkipAtp(inventoryQuantity, quantity)) {
    delete nextProperties[PROP_ESTIMATED_DELIVERY];
  } else if (!sku) {
    nextProperties[PROP_ESTIMATED_DELIVERY] = CALL_FOR_AVAILABILITY_TEXT;
  } else {
    try {
      const result = await getAtpDate({ sku, quantity });
      const propertyValue = result
        ? formatWeeksPropertyValue(
            result.estimatedDeliveryWeeksMin,
            result.estimatedDeliveryWeeksMax
          )
        : null;
      nextProperties[PROP_ESTIMATED_DELIVERY] =
        propertyValue || CALL_FOR_AVAILABILITY_TEXT;
    } catch {
      nextProperties[PROP_ESTIMATED_DELIVERY] = CALL_FOR_AVAILABILITY_TEXT;
    }
  }

  return nextProperties;
}

/**
 * @param {Record<string, string>} a
 * @param {Record<string, string>} b
 */
function propertiesEqual(a, b) {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(
    (key, index) => key === bKeys[index] && String(a[key]) === String(b[key])
  );
}

async function fetchCartJson() {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`);
  if (!response.ok) {
    throw new Error('Failed to fetch cart');
  }
  return response.json();
}

/**
 * Remember what should be attached on the next ATC.
 *
 * @param {{ sku?: string | null, inventoryQuantity?: number | null, estimatedDelivery?: string | null }} props
 */
function rememberPendingAtpLineProperties(props) {
  if ('sku' in props && props.sku != null && props.sku !== '') {
    pendingAtpLineProperties[PROP_ATP_SKU] = String(props.sku);
  }

  if (
    'inventoryQuantity' in props &&
    props.inventoryQuantity != null &&
    Number.isFinite(props.inventoryQuantity)
  ) {
    pendingAtpLineProperties[PROP_ATP_INVENTORY] = String(props.inventoryQuantity);
  }

  if ('estimatedDelivery' in props) {
    if (props.estimatedDelivery == null || props.estimatedDelivery === '') {
      delete pendingAtpLineProperties[PROP_ESTIMATED_DELIVERY];
    } else {
      pendingAtpLineProperties[PROP_ESTIMATED_DELIVERY] = String(
        props.estimatedDelivery
      );
    }
  }
}

/**
 * @param {CustomEvent} event
 */
function onVariantChange(event) {
  const formId = atpData?.formId;
  if (!formId || event.detail?.formId !== formId) {
    return;
  }

  updateAtpDisplay(formId);
}

/**
 * @param {Event} event
 */
function onQuantityEvent(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== 'quantity') {
    return;
  }

  const formId = atpData?.formId;
  if (!formId || !isQuantityInputForForm(target, formId)) {
    return;
  }

  updateAtpDisplay(formId);
}

/**
 * @param {HTMLInputElement} input
 * @param {string} formId
 */
function isQuantityInputForForm(input, formId) {
  const formAttr = input.getAttribute('form');
  if (formAttr) {
    return formAttr === formId;
  }

  return input.form?.id === formId;
}

/**
 * @param {string} formId
 */
function getSelectedVariantId(formId) {
  const formInput = document.querySelector(
    `form#${CSS.escape(formId)} input[name="id"]`
  );
  if (formInput?.value) {
    return String(formInput.value);
  }

  const linkedInput = document.querySelector(
    `input[name="id"][form="${CSS.escape(formId)}"]`
  );
  if (linkedInput?.value) {
    return String(linkedInput.value);
  }

  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant) {
    return urlVariant;
  }

  return null;
}

function getQuantity(formId) {
  const input =
    document.querySelector(`input[name="quantity"][form="${CSS.escape(formId)}"]`) ||
    document.querySelector(`form#${CSS.escape(formId)} input[name="quantity"]`);
  const qty = Number(input?.value);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

/**
 * @param {string} variantId
 * @returns {VariantAtpMeta | null}
 */
function getVariantMeta(variantId) {
  return atpData?.variants?.[String(variantId)] ?? null;
}

/**
 * @param {number} stock
 * @param {number} quantity
 */
function shouldSkipAtp(stock, quantity) {
  return stock >= quantity;
}

function invalidatePendingAtpResponse() {
  atpAbortController?.abort();
  atpAbortController = null;
  atpRequestId++;
}

/**
 * @param {string} formId
 * @returns {HTMLFormElement | null}
 */
function getProductForm(formId) {
  const form = document.getElementById(formId);
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} propertyKey
 * @param {string | number | null | undefined} value
 */
function upsertFormProperty(form, propertyKey, value) {
  const name = `properties[${propertyKey}]`;
  let input = Array.from(form.elements).find(
    (el) => el instanceof HTMLInputElement && el.name === name
  );

  if (value == null || value === '') {
    input?.remove();
    return;
  }

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = String(value);
}

/**
 * @param {string} formId
 * @param {{ sku?: string | null, inventoryQuantity?: number | null, estimatedDelivery?: string | null }} props
 */
function syncFormAtpProperties(formId, props) {
  rememberPendingAtpLineProperties(props);

  const form = getProductForm(formId);
  if (!form) {
    return;
  }

  if ('sku' in props && props.sku != null && props.sku !== '') {
    upsertFormProperty(form, PROP_ATP_SKU, props.sku);
  }

  if (
    'inventoryQuantity' in props &&
    props.inventoryQuantity != null &&
    Number.isFinite(props.inventoryQuantity)
  ) {
    upsertFormProperty(form, PROP_ATP_INVENTORY, props.inventoryQuantity);
  }

  if ('estimatedDelivery' in props) {
    upsertFormProperty(form, PROP_ESTIMATED_DELIVERY, props.estimatedDelivery ?? null);
  }
}

/**
 * @param {string} formId
 */
function clearEstimatedDeliveryProperty(formId) {
  rememberPendingAtpLineProperties({ estimatedDelivery: null });
  const form = getProductForm(formId);
  if (!form) {
    return;
  }
  upsertFormProperty(form, PROP_ESTIMATED_DELIVERY, null);
}

function clearMessage(formId) {
  const root = formId
    ? document.getElementById(`custom-atp-date-${formId}`)
    : null;
  const content = formId
    ? document.getElementById(`custom-atp-date-content-${formId}`)
    : null;
  if (!root || !content) {
    return;
  }

  content.textContent = '';
  root.hidden = true;
}

function showCallForAvailability(formId) {
  if (formId) {
    syncFormAtpProperties(formId, {
      estimatedDelivery: CALL_FOR_AVAILABILITY_TEXT,
    });
    showMessage(formId, CALL_FOR_AVAILABILITY_HTML);
  } else {
    rememberPendingAtpLineProperties({
      estimatedDelivery: CALL_FOR_AVAILABILITY_TEXT,
    });
  }
}

/**
 * @param {string} formId
 * @param {string} html
 */
function showMessage(formId, html) {
  const root = document.getElementById(`custom-atp-date-${formId}`);
  const content = document.getElementById(`custom-atp-date-content-${formId}`);
  if (!root || !content) {
    return;
  }

  content.innerHTML = html;
  root.hidden = false;
}

/**
 * Full sentence for PDP / quick-buy UI.
 * @param {number | null | undefined} min
 * @param {number | null | undefined} max
 */
function formatWeeksMessage(min, max) {
  if (min == null || max == null) {
    return null;
  }

  if (min === max) {
    return `Estimated delivery in ${min} weeks`;
  }

  return `Estimated delivery in ${min}–${max} weeks`;
}

/**
 * Key/Value-safe property value for cart + checkout (key is already "Estimated delivery").
 * @param {number | null | undefined} min
 * @param {number | null | undefined} max
 */
function formatWeeksPropertyValue(min, max) {
  if (min == null || max == null) {
    return null;
  }

  if (min === max) {
    return `in ${min} weeks`;
  }

  return `in ${min}–${max} weeks`;
}

/**
 * @param {string} formId
 */
async function updateAtpDisplay(formId) {
  const variantId = getSelectedVariantId(formId);
  if (!variantId) {
    invalidatePendingAtpResponse();
    clearEstimatedDeliveryProperty(formId);
    clearMessage(formId);
    return;
  }

  const meta = getVariantMeta(variantId);
  if (!meta) {
    invalidatePendingAtpResponse();
    clearEstimatedDeliveryProperty(formId);
    clearMessage(formId);
    return;
  }

  const quantity = getQuantity(formId);
  const { sku, inventoryQuantity } = meta;

  syncFormAtpProperties(formId, {
    sku,
    inventoryQuantity,
  });

  showDebugCustomAtpDateOutput &&
    console.log('DEBUG updateAtpDisplay', { variantId, meta, quantity });

  if (shouldSkipAtp(inventoryQuantity, quantity)) {
    invalidatePendingAtpResponse();
    clearEstimatedDeliveryProperty(formId);
    clearMessage(formId);
    return;
  }

  if (!sku) {
    invalidatePendingAtpResponse();
    showCallForAvailability(formId);
    return;
  }

  const currentRequestId = ++atpRequestId;
  atpAbortController?.abort();
  atpAbortController = new AbortController();

  try {
    const result = await getAtpDate({
      sku,
      quantity,
      signal: atpAbortController.signal,
    });

    if (currentRequestId !== atpRequestId) {
      return;
    }

    if (!result) {
      showCallForAvailability(formId);
      return;
    }

    const uiMessage = formatWeeksMessage(
      result.estimatedDeliveryWeeksMin,
      result.estimatedDeliveryWeeksMax
    );
    const propertyValue = formatWeeksPropertyValue(
      result.estimatedDeliveryWeeksMin,
      result.estimatedDeliveryWeeksMax
    );

    if (uiMessage && propertyValue) {
      syncFormAtpProperties(formId, {
        sku,
        inventoryQuantity,
        estimatedDelivery: propertyValue,
      });
      showMessage(formId, uiMessage);
    } else {
      showCallForAvailability(formId);
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }

    showDebugCustomAtpDateOutput && console.error('customAtpDate:', error);
    showCallForAvailability(formId);
  }
}

async function getAtpDate({
  sku,
  quantity,
  sellingLocationId = undefined,
  postalCode = undefined,
  signal = undefined,
}) {
  const request = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:
        'Bearer 722122FF46D8012AB7955632E64A997A6E80051CAE20A6E785E2BE225685CE0B',
      origin: 'https://babettes.endertech.net',
    },
    body: JSON.stringify({
      sku: sku,
      quantity: quantity,
      sellingLocationId: sellingLocationId,
      postalCode: postalCode,
    }),
    signal,
  };

  const response = await fetch(
    'https://babettes.endertech.net/api/product-delivery-date',
    request
  );
  if (!response.ok) {
    throw new Error('Something went wrong with the request');
  }

  const responseJson = await response?.json();

  showDebugCustomAtpDateOutput &&
    console.log('DEBUG: deliveryDate', responseJson.deliveryDate);
  showDebugCustomAtpDateOutput &&
    console.log(
      'DEBUG: estimatedDeliveryWeeksMax',
      responseJson.estimatedDeliveryWeeksMax
    );
  showDebugCustomAtpDateOutput &&
    console.log(
      'DEBUG: estimatedDeliveryWeeksMin',
      responseJson.estimatedDeliveryWeeksMin
    );

  if (!responseJson.deliveryDate) {
    showDebugCustomAtpDateOutput && console.log('DEBUG: No delivery date found');
    return null;
  }

  if (
    responseJson.estimatedDeliveryWeeksMax == null ||
    responseJson.estimatedDeliveryWeeksMax === ''
  ) {
    showDebugCustomAtpDateOutput &&
      console.log('DEBUG: Estimated delivery weeks max is not set');
    return null;
  }

  if (
    responseJson.estimatedDeliveryWeeksMin == null ||
    responseJson.estimatedDeliveryWeeksMin === ''
  ) {
    showDebugCustomAtpDateOutput &&
      console.log('DEBUG: Estimated delivery weeks min is not set');
    return null;
  }

  return {
    deliveryDate: responseJson.deliveryDate,
    estimatedDeliveryWeeksMax: responseJson.estimatedDeliveryWeeksMax,
    estimatedDeliveryWeeksMin: responseJson.estimatedDeliveryWeeksMin,
  };
}
